/** Utilidades fiscales españolas (frontend) — alineadas con backend fiscalTax.js */

export const SPANISH_IVA_RATES = [0, 4, 10, 21] as const;

export type IvaRate = (typeof SPANISH_IVA_RATES)[number];
export type ExpenseType = 'no_suplido' | 'suplido';
export const roundMoney = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
};

export const normalizeIvaRate = (rate: number): IvaRate => {
  const r = Number(rate);
  if (!Number.isFinite(r) || r < 0) return 21;
  return (SPANISH_IVA_RATES as readonly number[]).includes(r) ? (r as IvaRate) : 21;
};

export const normalizeIrpfRate = (rate: number): number => {
  const r = Number(rate);
  if (!Number.isFinite(r) || r < 0 || r > 100) return 0;
  return r;
};

export interface InvoiceTaxResult {
  subtotal: number;
  ivaRate: number;
  ivaAmount: number;
  irpfRate: number;
  irpfAmount: number;
  suplidosAmount: number;
  total: number;
}

export function calculateInvoiceTaxes(
  subtotal: number,
  ivaRate = 21,
  irpfRate = 0,
  suplidos = 0
): InvoiceTaxResult {
  const base = roundMoney(subtotal);
  const iva = normalizeIvaRate(ivaRate);
  const irpf = normalizeIrpfRate(irpfRate);
  const sup = roundMoney(Math.max(0, suplidos));
  const ivaAmount = roundMoney((base * iva) / 100);
  const irpfAmount = roundMoney((base * irpf) / 100);
  const total = roundMoney(base + ivaAmount - irpfAmount + sup);
  return {
    subtotal: base,
    ivaRate: iva,
    ivaAmount,
    irpfRate: irpf,
    irpfAmount,
    suplidosAmount: sup,
    total
  };
}

export type InvoiceLineInput = {
  description?: string;
  importe?: number;
  quantity?: number;
  unitPrice?: number;
  subtotal?: number;
  tipo?: string;
  es_suplido?: boolean;
  esSuplido?: boolean;
  expense_type?: ExpenseType;
  expenseType?: ExpenseType;
  ticket_total?: number;
  ticketTotal?: number;
  ticket_base_amount?: number;
  ticketBaseAmount?: number;
  ticket_vat_amount?: number;
  ticketVatAmount?: number;
  ticket_vat_rate?: number;
  ticketVatRate?: number;
  entryMode?: 'base' | 'totalIva';
  incluidoEnBase?: boolean;
  incluido_en_base?: boolean;
  documentoRef?: string;
};

const normalizeCategoryKey = (value: string) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

const VAT_10_CATEGORY_KEYS = new Set([
  "transporte", "gasolina", "taxi", "peaje", "peajes", "parking",
  "autobus", "metro", "tren", "vuelo", "vuelos", "combustible", "kilometraje"
]);

const TRANSPORT_TEXT_KEYWORDS =
  /\b(taxi|uber|cabify|bolt|transporte|gasolin|peaje|autopista|aparcamiento|parking|metro|autobus|tren|ave|renfe|vuelo|aeropuerto|kilometraje|km\b)\b/i;

export function defaultVatRateFromCategory(categoria: string): IvaRate {
  const key = normalizeCategoryKey(categoria);
  if (VAT_10_CATEGORY_KEYS.has(key)) return 10;
  return 21;
}

export function inferVatRateFromText(...parts: Array<string | null | undefined>): IvaRate | null {
  const joined = parts.filter(Boolean).join(" ");
  if (TRANSPORT_TEXT_KEYWORDS.test(joined)) return 10;
  return null;
}

export function resolveLineIvaRate(
  line: InvoiceLineInput & { categoria?: string; proveedor?: string; descripcion?: string },
  defaultIvaRate = 21
): IvaRate {
  const explicit = line.ticket_vat_rate ?? line.ticketVatRate;
  if (explicit != null && Number.isFinite(Number(explicit))) return normalizeIvaRate(Number(explicit));
  const fromText = inferVatRateFromText(line.description, line.descripcion, line.proveedor, line.categoria);
  if (fromText != null) return fromText;
  const tipo = line.tipo || (line as { type?: string }).type || line.categoria;
  if (tipo) return defaultVatRateFromCategory(tipo);
  return normalizeIvaRate(defaultIvaRate);
}

export interface IssuedInvoiceTotals extends InvoiceTaxResult {
  servicesTotal: number;
  baseTrabajo: number;
  reimbursableTotal: number;
  baseGastosNoSuplidos: number;
  baseImponibleTotal: number;
  breakdown: {
    servicios: number;
    baseTrabajo: number;
    gastosRepercutidos: number;
    baseGastosNoSuplidos: number;
    baseImponible: number;
    iva: number;
    irpf: number;
    suplidos: number;
    totalFactura: number;
  };
}

const lineImporte = (line: InvoiceLineInput): number => {
  if (line.importe != null) return roundMoney(line.importe);
  const qty = Math.max(0, Number(line.quantity || 1));
  const unit = Math.max(0, Number(line.unitPrice || 0));
  if (qty * unit > 0) return roundMoney(qty * unit);
  return roundMoney(line.subtotal || 0);
};

export function baseFromTotalWithVat(total: number, ivaRate = 21): number {
  const t = roundMoney(total);
  if (t <= 0) return 0;
  const rate = normalizeIvaRate(ivaRate);
  return roundMoney(t / (1 + rate / 100));
}

export function computeTicketAmounts(
  ticketTotal: number,
  ticketVatRate = 21,
  ticketBaseAmount: number | null = null
) {
  const total = roundMoney(ticketTotal);
  const rate = normalizeIvaRate(ticketVatRate);
  let base =
    ticketBaseAmount != null && Number.isFinite(ticketBaseAmount)
      ? roundMoney(ticketBaseAmount)
      : null;
  if (base == null && total > 0) {
    base = baseFromTotalWithVat(total, rate);
  }
  if (base == null) base = 0;
  const vat = roundMoney(Math.max(0, total - base));
  return {
    ticket_total: total,
    ticket_base_amount: base,
    ticket_vat_amount: vat,
    ticket_vat_rate: rate
  };
}

export function resolveReimbursableBase(line: InvoiceLineInput): number {
  if (!line || line.es_suplido || line.esSuplido) return 0;

  const explicitBase = line.ticket_base_amount ?? line.ticketBaseAmount;
  if (explicitBase != null && Number.isFinite(Number(explicitBase))) {
    return roundMoney(explicitBase);
  }

  const ticketTotal = line.ticket_total ?? line.ticketTotal;
  const ticketVatRate = line.ticket_vat_rate ?? line.ticketVatRate ?? 21;
  if (ticketTotal != null && Number.isFinite(Number(ticketTotal)) && Number(ticketTotal) > 0) {
    return baseFromTotalWithVat(ticketTotal, ticketVatRate);
  }

  if (line.entryMode === 'totalIva') {
    return baseFromTotalWithVat(lineImporte(line), ticketVatRate);
  }

  return lineImporte(line);
}

export function resolveSuplidoAmount(line: InvoiceLineInput): number {
  const ticketTotal = line.ticket_total ?? line.ticketTotal;
  if (ticketTotal != null && Number.isFinite(Number(ticketTotal))) {
    return roundMoney(ticketTotal);
  }
  return lineImporte(line);
}

export function calculateIssuedInvoiceTotals(params: {
  services?: InvoiceLineInput[];
  reimbursableExpenses?: InvoiceLineInput[];
  suplidos?: InvoiceLineInput[];
  ivaRate?: number;
  irpfRate?: number;
}): IssuedInvoiceTotals {
  const services = params.services || [];
  const reimbursableExpenses = params.reimbursableExpenses || [];
  const suplidos = params.suplidos || [];
  const servicesTotal = roundMoney(services.reduce((s, l) => s + lineImporte(l), 0));
  const reimbursableTotal = roundMoney(
    reimbursableExpenses
      .filter((l) => !l.es_suplido && !l.esSuplido)
      .filter((l) => (l.expense_type ?? l.expenseType) !== 'suplido')
      .filter(
        (l) =>
          (l as { incluidoEnBase?: boolean }).incluidoEnBase !== false &&
          (l as { incluido_en_base?: boolean }).incluido_en_base !== false
      )
      .reduce((s, l) => s + resolveReimbursableBase(l), 0)
  );
  const baseImponibleTotal = roundMoney(servicesTotal + reimbursableTotal);
  const suplidosAmount = roundMoney(suplidos.reduce((s, l) => s + resolveSuplidoAmount(l), 0));
  const defaultIva = params.ivaRate ?? 21;
  const taxableServices = services.filter(
    (l) => (l as { incluidoEnBase?: boolean }).incluidoEnBase !== false &&
      (l as { incluido_en_base?: boolean }).incluido_en_base !== false
  );
  const taxableReimb = reimbursableExpenses
    .filter((l) => !l.es_suplido && !l.esSuplido)
    .filter((l) => (l.expense_type ?? l.expenseType) !== 'suplido')
    .filter(
      (l) =>
        (l as { incluidoEnBase?: boolean }).incluidoEnBase !== false &&
        (l as { incluido_en_base?: boolean }).incluido_en_base !== false
    );
  const ivaAmount = roundMoney(
    taxableServices.reduce(
      (s, l) => s + roundMoney((lineImporte(l) * resolveLineIvaRate(l, defaultIva)) / 100),
      0
    ) +
      taxableReimb.reduce(
        (s, l) =>
          s + roundMoney((resolveReimbursableBase(l) * resolveLineIvaRate(l, defaultIva)) / 100),
        0
      )
  );
  const irpfRate = normalizeIrpfRate(params.irpfRate ?? 0);
  const irpfAmount = roundMoney((baseImponibleTotal * irpfRate) / 100);
  const total = roundMoney(baseImponibleTotal + ivaAmount - irpfAmount + suplidosAmount);
  const tax = {
    subtotal: baseImponibleTotal,
    ivaRate: normalizeIvaRate(defaultIva),
    ivaAmount,
    irpfRate,
    irpfAmount,
    suplidosAmount,
    total
  };
  return {
    ...tax,
    servicesTotal,
    baseTrabajo: servicesTotal,
    reimbursableTotal,
    baseGastosNoSuplidos: reimbursableTotal,
    baseImponibleTotal,
    breakdown: {
      servicios: servicesTotal,
      baseTrabajo: servicesTotal,
      gastosRepercutidos: reimbursableTotal,
      baseGastosNoSuplidos: reimbursableTotal,
      baseImponible: baseImponibleTotal,
      iva: tax.ivaAmount,
      irpf: tax.irpfAmount,
      suplidos: suplidosAmount,
      totalFactura: tax.total
    }
  };
}

export function partitionExpenseRows(
  rows: Array<{
    description: string;
    tipo?: string;
    expenseType: ExpenseType;
    ticketTotal: string;
    ticketVatRate: number;
    documentoRef?: string;
  }>,
  parseAmount: (v: string) => number
) {
  const reimbursableExpenses: InvoiceLineInput[] = [];
  const suplidos: InvoiceLineInput[] = [];
  for (const row of rows) {
    if (!row.description.trim()) continue;
    const total = parseAmount(row.ticketTotal);
    if (row.expenseType === 'suplido') {
      suplidos.push({
        description: row.description,
        tipo: row.tipo,
        ticket_total: total,
        importe: total,
        documentoRef: row.documentoRef
      });
    } else {
      const ticket = computeTicketAmounts(total, row.ticketVatRate);
      reimbursableExpenses.push({
        description: row.description,
        tipo: row.tipo,
        expense_type: 'no_suplido',
        ticket_total: ticket.ticket_total,
        ticket_base_amount: ticket.ticket_base_amount,
        ticket_vat_amount: ticket.ticket_vat_amount,
        ticket_vat_rate: ticket.ticket_vat_rate,
        importe: ticket.ticket_base_amount
      });
    }
  }
  return { reimbursableExpenses, suplidos };
}
